// One-off fundraising projects (uniforms, a trip, an event) and the contributions to them.
// Money is ALWAYS integer ngwee here. A project belongs to one department, or to the whole
// church (department_id null, created by central treasury only) and then accepts contributions
// from any department. A contribution is a ledger row: it can be cancelled only with a visible
// reversal (see reversePayment in dues.ts), never edited or deleted.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'
import type { PaymentMethod } from './dues'

export interface Project {
  id: string
  /** null = church-wide. */
  departmentId: string | null
  name: string
  description: string | null
  targetNgwee: number | null
  startsOn: string | null
  endsOn: string | null
  closedAt: string | null
  /** Raised by THIS department so far (reversed gifts already netted off). */
  raisedNgwee: number
}

type Row = {
  id: string
  department_id: string | null
  name: string
  description: string | null
  target_ngwee: number | null
  starts_on: string | null
  ends_on: string | null
  closed_at: string | null
}

const COLS = 'id, department_id, name, description, target_ngwee, starts_on, ends_on, closed_at'

/** Projects a department can give to: its own and the church-wide ones, open ones first. */
export async function listProjects(departmentId: string): Promise<Project[]> {
  const [rows, gifts] = await Promise.all([
    supabase.from('project').select(COLS).is('archived_at', null).or(`department_id.eq.${departmentId},department_id.is.null`).order('created_at', { ascending: false }),
    supabase.from('contribution').select('project_id, amount_ngwee').eq('department_id', departmentId).eq('kind', 'project').in('status', ['posted', 'reversed']),
  ])
  const raised = new Map<string, number>()
  for (const g of unwrap(gifts as never, 'Could not load what has been given.') as { project_id: string; amount_ngwee: number }[]) {
    raised.set(g.project_id, (raised.get(g.project_id) ?? 0) + Number(g.amount_ngwee))
  }
  return (unwrap(rows as never, 'Could not load the projects.') as Row[])
    .map((r) => ({
      id: r.id,
      departmentId: r.department_id,
      name: r.name,
      description: r.description,
      targetNgwee: r.target_ngwee === null ? null : Number(r.target_ngwee),
      startsOn: r.starts_on,
      endsOn: r.ends_on,
      closedAt: r.closed_at,
      raisedNgwee: raised.get(r.id) ?? 0,
    }))
    .sort((a, b) => Number(a.closedAt !== null) - Number(b.closedAt !== null))
}

export async function getProject(departmentId: string, projectId: string): Promise<Project | null> {
  return (await listProjects(departmentId)).find((p) => p.id === projectId) ?? null
}

export interface ProjectInput {
  name: string
  description?: string
  targetNgwee?: number | null
  startsOn?: string | null
  endsOn?: string | null
}

/**
 * Start a project. `departmentId` null makes it church-wide (central treasury only). `clientRef` makes
 * a retry whose first answer was lost find the project it already made instead of making a second.
 */
export async function createProject(departmentId: string | null, p: ProjectInput, clientRef: string): Promise<string> {
  if (p.targetNgwee != null && (!Number.isSafeInteger(p.targetNgwee) || p.targetNgwee <= 0)) {
    throw new DataError({ code: 'P0001', message: 'The target must be greater than zero.' })
  }
  const userId = await currentUserId()
  const fields = {
    name: p.name.trim(),
    description: p.description?.trim() ? p.description.trim() : null,
    target_ngwee: p.targetNgwee ?? null,
    starts_on: p.startsOn || null,
    ends_on: p.endsOn || null,
  }
  const res = await supabase
    .from('project')
    .insert({ department_id: departmentId, ...fields, created_by: userId, client_ref: clientRef })
    .select('id')
    .single()
  if (res.error) {
    if (res.error.code === '23505') {
      const prior = await supabase.from('project').select('id').eq('created_by', userId).eq('client_ref', clientRef).maybeSingle()
      if (prior.data) {
        const id = (prior.data as { id: string }).id
        await supabase.from('project').update(fields).eq('id', id)
        return id
      }
    }
    throw new DataError(res.error, 'Could not start the project.')
  }
  return (res.data as { id: string }).id
}

/** Move a project's end date (or clear it). Gifts dated after the end date are refused, so this reopens or extends it. */
export async function setProjectEndDate(id: string, endsOn: string | null): Promise<void> {
  const res = await supabase.from('project').update({ ends_on: endsOn || null }).eq('id', id).select('id')
  if (res.error) throw new DataError(res.error, 'Could not change the end date.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that project.' })
}

/** Stop taking new gifts. Existing gifts and any later reversal still work. */
export async function closeProject(id: string): Promise<void> {
  const res = await supabase.from('project').update({ closed_at: new Date().toISOString() }).eq('id', id).is('closed_at', null).select('id')
  if (res.error) throw new DataError(res.error, 'Could not close the project.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot close that project, or it is already closed.' })
}

export interface Gift {
  id: string
  memberId: string
  memberName: string
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
  reference: string | null
  status: 'posted' | 'reversed'
  /** The negative entry that corrects an earlier gift. */
  reversesId: string | null
  reversalReason: string | null
}

type GiftRow = {
  id: string
  member_id: string
  amount_ngwee: number
  method: PaymentMethod
  received_on: string
  reference: string | null
  status: 'posted' | 'reversed'
  reverses_id: string | null
  reversal_reason: string | null
  member: { full_name: string } | null
}

/** Gifts this department recorded for a project, newest first. */
export async function listGifts(departmentId: string, projectId: string): Promise<Gift[]> {
  const res = await supabase
    .from('contribution')
    .select('id, member_id, amount_ngwee, method, received_on, reference, status, reverses_id, reversal_reason, member:member_id(full_name)')
    .eq('department_id', departmentId)
    .eq('project_id', projectId)
    .in('status', ['posted', 'reversed'])
    .order('received_on', { ascending: false })
    .order('created_at', { ascending: false })
  return (unwrap(res as never, 'Could not load the gifts.') as GiftRow[]).map((r) => ({
    id: r.id,
    memberId: r.member_id,
    memberName: r.member?.full_name ?? 'Unnamed member',
    amountNgwee: Number(r.amount_ngwee),
    method: r.method,
    receivedOn: r.received_on,
    reference: r.reference,
    status: r.status,
    reversesId: r.reverses_id,
    reversalReason: r.reversal_reason,
  }))
}

export interface GiftInput {
  memberId: string
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
  reference?: string
}

/**
 * Record a gift to a project. `clientRef` makes a retry (double tap, lost connection) return the
 * same row instead of recording the money twice.
 */
export async function recordGift(departmentId: string, projectId: string, g: GiftInput, clientRef: string): Promise<string> {
  if (!Number.isSafeInteger(g.amountNgwee) || g.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'Enter an amount greater than zero.' })
  }
  const userId = await currentUserId()
  const res = await supabase
    .from('contribution')
    .insert({
      department_id: departmentId,
      kind: 'project',
      project_id: projectId,
      member_id: g.memberId,
      amount_ngwee: g.amountNgwee,
      method: g.method,
      received_on: g.receivedOn,
      reference: g.reference?.trim() ? g.reference.trim() : null,
      client_ref: clientRef,
      created_by: userId,
    })
    .select('id')
    .single()
  if (res.error) {
    if (res.error.code === '23505') {
      const prior = await supabase.from('contribution').select('id').eq('department_id', departmentId).eq('client_ref', clientRef).maybeSingle()
      if (prior.data) return (prior.data as { id: string }).id
    }
    throw new DataError(res.error, 'Could not record the gift.')
  }
  return (res.data as { id: string }).id
}
